import { SUPERADMIN_PERMISSIONS, ROUTE_PERMISSIONS } from "../Constants/superadminPermission";
import { PERMISSIONS } from "../Constants/permission";
import CryptoJS from "crypto-js";
import { SECRET_KEY } from "../Constants/Config";
import { getAuthDetails } from "../Common/cookiesHelper";

/**
 * Get permissions from localStorage (decrypted)
 * @returns {string[]} Array of permission codes
 */
export const getPermissionsFromStorage = () => {
  try {
    const encryptedPermissions = localStorage.getItem("permissions");
    if (!encryptedPermissions) return [];

    const decryptedBytes = CryptoJS.AES.decrypt(encryptedPermissions, SECRET_KEY);
    const decryptedPermissions = decryptedBytes.toString(CryptoJS.enc.Utf8);

    if (!decryptedPermissions) return [];

    return decryptedPermissions.split(",").map((p) => p.trim()).filter(Boolean);
  } catch (error) {
    console.warn("Failed to decrypt permissions:", error.message);
    return [];
  }
};

/** Check if user has a specific permission code */
export const hasPermission = (permissionCode) => {
  const userPermissions = getPermissionsFromStorage();
  return userPermissions.includes(permissionCode);
};

/** Check if user has ANY of the provided permissions */
export const hasAnyPermission = (permissionCodes) => {
  const userPermissions = getPermissionsFromStorage();
  return permissionCodes.some((code) => userPermissions.includes(code));
};

/** Check if user has ALL of the provided permissions */
export const hasAllPermissions = (permissionCodes) => {
  const userPermissions = getPermissionsFromStorage();
  return permissionCodes.every((code) => userPermissions.includes(code));
};

/** Check permission using SUPERADMIN_PERMISSIONS key (e.g., "ACCESS_NEWSLETTER") */
export const hasSuperadminPermission = (permissionKey) => {
  const permissionCode = SUPERADMIN_PERMISSIONS[permissionKey];
  if (!permissionCode) return false;
  return hasPermission(permissionCode);
};

/** Check permission using PERMISSIONS key (e.g., "MENU_NO_SALE") */
export const hasMenuPermission = (permissionKey) => {
  const permissionCode = PERMISSIONS[permissionKey];
  if (!permissionCode) return false;
  return hasPermission(permissionCode);
};

/**
 * Get the current user's login_type using getAuthDetails (from cookiesHelper)
 * @returns {string} login_type (e.g., "superadmin", "merchant", "admin", "manager")
 */
export const getLoginType = () => {
  try {
    const { userTypeData } = getAuthDetails();
    return userTypeData?.login_type || "";
  } catch {
    return "";
  }
};

/** Check if current user is superadmin */
export const isSuperadminUser = () => getLoginType() === "superadmin";

/**
 * Check if user has permission for a route key (e.g., "newsletter", "defaults")
 * Permission checks only apply to superadmin users; all other login types get full access.
 */
export const hasRoutePermission = (routeKey) => {
  if (!isSuperadminUser()) return true;
  const requiredPermission = ROUTE_PERMISSIONS[routeKey];
  if (!requiredPermission) return true;
  return hasPermission(requiredPermission);
};

/**
 * Check if user has permission for a link path (e.g., "/unapprove/defaults")
 * Permission checks only apply to superadmin users; all other login types get full access.
 */
export const hasLinkPermission = (linkPath) => {
  if (!isSuperadminUser()) return true;
  if (!linkPath) return true;
  const segments = linkPath.split("/").filter(Boolean);
  const routeKey = segments[segments.length - 1];
  return hasRoutePermission(routeKey);
};

/** Clear permissions from localStorage */
export const clearPermissions = () => {
  localStorage.removeItem("permissions");
};
